import React, { useEffect, useState } from 'react'
import { Spinner } from 'react-bootstrap'
import { Link, useHistory } from 'react-router-dom'
import ReactPaginate from 'react-paginate'
import { Badge, Button, Card, CardBody, CardHeader, Col, Input, Row, Table } from 'reactstrap'
import { useFetch } from '../../hooks'
import { api } from '../../utils'
import { ToastContainer } from 'react-toastify'
import Header from '../../components/Headers/Header'

const LLMVisibilityListing = () => {
  const history = useHistory()
  const itemPerPage = 10

  const [pages, setPages] = useState(0)
  const [tableData, setTableData] = useState([])
  const [actualPage, setActualPage] = useState(1)
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')

  const { data, info, loading, error } = useFetch({
    url: 'llm-visibility/',
    params: { page_size: itemPerPage },
  })

  useEffect(() => {
    if (data.length > 0 && tableData.length === 0) {
      setTableData(data)
      setPages(Math.ceil(info.count / itemPerPage))
    }
  }, [data, info, tableData])

  const handlePageClick = (data) => {
    const currentPage = data.selected + 1
    setActualPage(currentPage)
    api
      .get('llm-visibility/', {
        params: {
          page_size: itemPerPage,
          page: currentPage,
        },
      })
      .then((response) => {
        setTableData(response.data.results)
        setPages(Math.ceil(response.data.count / itemPerPage))
      })
      .catch((error) => console.error(error))
  }

  const getStatusBadge = (status) => {
    switch (status) {
      case 'EXPORTED':
        return 'success'
      case 'EXPORTED_QUESTIONS':
        return 'info'
      case 'ERROR':
        return 'danger'
      case 'CREATED':
        return 'primary'
      default:
        return 'warning'
    }
  }

  const getStatusLabel = (status) => {
    switch (status) {
      case 'EXPORTED_QUESTIONS':
        return 'EXPORTED (QUESTIONS ONLY)'
      default:
        return status
    }
  }

  const filteredData = tableData.filter((item) => {
    const matchesSearch = (item.name || '')
      .toLowerCase()
      .includes(searchTerm.trim().toLowerCase())
    const matchesStatus = statusFilter === 'all' || item.status === statusFilter
    return matchesSearch && matchesStatus
  })

  const summary = tableData.reduce(
    (acc, item) => {
      if (item.status === 'EXPORTED') acc.exported += 1
      if (item.status === 'EXPORTED_QUESTIONS') acc.questionsOnly += 1
      if (item.status === 'ERROR') acc.errors += 1
      return acc
    },
    { exported: 0, questionsOnly: 0, errors: 0 }
  )

  const getRunType = (status) => {
    if (status === 'EXPORTED_QUESTIONS') return '❓ Questions Only'
    return '✅ Full Report'
  }

  return (
    <Header
      iconClass="ni ni-align-left-2 text-white"
      title="LLM Visibility"
      description={"Dato un set di Keyword, genera delle domande per gli LLM e controlla quanto i brand vengono citati in esse"}
    >
      {loading && <div className="text-center"><Spinner animation="grow" /></div>}
      {!loading && error && (
        <Row>
          <Col>
            <Card className="shadow mb-4">
              <CardBody className="text-danger">Unable to load LLM Visibility reports.</CardBody>
            </Card>
          </Col>
        </Row>
      )}
      {tableData && (
        <Row style={{ marginBottom: '100px' }}>
          <div className="col pb-5">
            <Card className="shadow">
              <CardHeader className="border-0">
                <Row className="align-items-center">
                  <Col xs="7">
                    <h1 className="mb-0">LLM Visibility Reports</h1>
                  </Col>
                  <Col className="text-right" xs="5">
                    <Button
                      className="mr-2"
                      color="secondary"
                      onClick={() => history.push('/llm-visibility-scheduled')}
                      size="md"
                    >
                      Scheduled View
                    </Button>
                    <Button
                      color="info"
                      onClick={() => history.push('/add-llm-visibility/')}
                      size="md"
                    >
                      + Add New
                    </Button>
                  </Col>
                </Row>
                <Row className="mt-4">
                  <Col md="3">
                    <Card className="shadow-sm border-0 mb-0">
                      <CardBody className="py-3">
                        <small className="text-muted">On this page</small>
                        <div className="h3 mb-0">{tableData.length}</div>
                      </CardBody>
                    </Card>
                  </Col>
                  <Col md="3">
                    <Card className="shadow-sm border-0 mb-0">
                      <CardBody className="py-3">
                        <small className="text-muted">Exported</small>
                        <div className="h3 mb-0 text-success">{summary.exported}</div>
                      </CardBody>
                    </Card>
                  </Col>
                  <Col md="3">
                    <Card className="shadow-sm border-0 mb-0">
                      <CardBody className="py-3">
                        <small className="text-muted">Questions Only</small>
                        <div className="h3 mb-0 text-primary">{summary.questionsOnly}</div>
                      </CardBody>
                    </Card>
                  </Col>
                  <Col md="3">
                    <Card className="shadow-sm border-0 mb-0">
                      <CardBody className="py-3">
                        <small className="text-muted">Errors</small>
                        <div className="h3 mb-0 text-danger">{summary.errors}</div>
                      </CardBody>
                    </Card>
                  </Col>
                </Row>
                <Row className="mt-3">
                  <Col md="6">
                    <Input
                      type="text"
                      placeholder="Search report name"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </Col>
                  <Col md="6">
                    <Input
                      type="select"
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                    >
                      <option value="all">All Statuses</option>
                      <option value="EXPORTED">EXPORTED</option>
                      <option value="EXPORTED_QUESTIONS">EXPORTED_QUESTIONS</option>
                      <option value="ERROR">ERROR</option>
                      <option value="CREATED">CREATED</option>
                      <option value="RETRIEVING_QUESTIONS">RETRIEVING_QUESTIONS</option>
                      <option value="RETRIEVING_ANSWERS">RETRIEVING_ANSWERS</option>
                    </Input>
                  </Col>
                </Row>
              </CardHeader>
              <Table className="align-items-center table-flush" responsive>
                <thead className="thead-light">
                  <tr>
                    <th scope="col">Name</th>
                    <th scope="col">Date</th>
                    <th scope="col">Type</th>
                    <th scope="col">Connected Tagging</th>
                    <th scope="col">Status</th>
                    <th scope="col">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredData.length === 0 && (
                    <tr>
                      <td colSpan="6" className="text-center text-muted">
                        No reports found for current filters.
                      </td>
                    </tr>
                  )}
                  {filteredData.map((item) => (
                    <tr key={item.id}>
                      <th scope="row">
                        <Link to={`/add-llm-visibility/${item.id}`}>{item.name}</Link>
                      </th>
                      <td>{new Date(item.created).toLocaleString()}</td>
                      <td>
                        <Badge color={item.status === 'EXPORTED_QUESTIONS' ? 'primary' : 'dark'} pill className="text-white">
                          {getRunType(item.status)}
                        </Badge>
                      </td>
                      <td>
                        {item.connected_tagging ? (
                          <Link to={`/reporting/reports/seo/${item.connected_tagging}`}>View Report</Link>
                        ) : (
                          'N/A'
                        )}
                      </td>
                      <td>
                        <Badge color={getStatusBadge(item.status)} pill className="text-white">
                          {getStatusLabel(item.status)}
                        </Badge>
                      </td>
                      <td>
                        <Button
                          color="default"
                          size="sm"
                          onClick={() => history.push(`/add-llm-visibility/${item.id}`)}
                        >
                          Open
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
              <ReactPaginate
                previousLabel={'<'}
                nextLabel={'>'}
                breakLabel={'...'}
                pageCount={pages}
                forcePage={actualPage - 1}
                onPageChange={handlePageClick}
                containerClassName={'pagination justify-content-center pt-5 pb-3'}
                pageClassName={'page-item'}
                pageLinkClassName={'page-link'}
                previousClassName={'page-item'}
                previousLinkClassName={'page-link'}
                nextClassName={'page-item'}
                nextLinkClassName={'page-link'}
                breakClassName={'page-item'}
                breakLinkClassName={'page-link'}
                activeClassName={'active'}
              />
            </Card>
          </div>
        </Row>
      )}
      <ToastContainer />
    </Header>
  )
}

export default LLMVisibilityListing
